<template>
  <ModalQmulus
    :visible="visible"
    :title="title"
    width="75rem"
    max-width="calc(100dvw - 2rem)"
    close-on-escape
    data-qa="modal-release-highlights"
    @update:visible="emit('update:visible', $event)"
    @close="emit('close')"
  >
    <QmulusCarousel
      v-model="shownSlideIndex"
      has-global-keyboard-navigation
    >
      <template
        v-for="(slide, index) in slides"
        :key="index"
      >
        <ReleaseHighlight
          v-if="slide.type === 'highlight'"
          :highlight="slide.highlight"
          :media-folder-url="`${mediaBaseUrl}/${slide.version}`"
          :version="slide.version"
          is-slide
        />
        <section
          v-else
          class="modal-release-highlights__release-notes"
          data-qa="modal-release-highlights__release-notes"
        >
          <div class="modal-release-highlights__release-notes-content">
            <ReleaseMarkdown :markdown="slide.releaseNotes" />
          </div>
        </section>
      </template>
    </QmulusCarousel>
    <template #actions>
      <div class="modal-release-highlights__actions flex-row align-center flex-wrap w-full">
        <CheckboxQmulus
          v-tooltip="optOutHint"
          :model-value="hasOptedOut"
          :label="t('doNotShowAfterUpdates')"
          data-qa="modal-release-highlights__opt-out"
          @update:model-value="emit('update:hasOptedOut', $event)"
        />
        <router-link
          :to="allReleasesLink"
          data-qa="modal-release-highlights__all-releases"
          @click="emit('update:visible', false)"
        >
          {{ t('showAllReleases') }}
        </router-link>
        <SemanticButton
          variant="close"
          @click="emit('update:visible', false)"
        />
      </div>
    </template>
  </ModalQmulus>
</template>

<script setup lang="ts">
import {
  computed,
  shallowRef,
  watch,
} from 'vue';
import type {
  RouteLocationRaw,
} from 'vue-router';

import type {
  TQTooltipBinding,
} from '../../directives/q-tooltip/q-tooltip.ts';
import useLibraryI18n from '../../i18n/composable.ts';
import SemanticButton from '../button/semantic-button.vue';
import QmulusCarousel from '../carousel/qmulus-carousel.vue';
import CheckboxQmulus from '../checkbox-qmulus/checkbox-qmulus.vue';
import ModalQmulus from '../modal-qmulus/modal-qmulus.vue';

import ReleaseHighlight from './release-highlight.vue';
import {
  type TRelease,
  type TReleaseHighlight,
  selectReleases,
} from './release-highlights.ts';
import ReleaseMarkdown from './release-markdown.vue';

const props = defineProps<{
  visible: boolean,
  /** See `ReleaseHighlightsList`. */
  releases: TRelease[],
  currentVersion: string,
  lastKnownVersion?: string,
  mediaBaseUrl: string,
  /** State of the "Don't show for future updates" checkbox. */
  hasOptedOut: boolean,
  /**
   * Tooltip of the checkbox. The label alone reads as if only this release were skipped, so the
   * hint explains that it affects all future ones and where the highlights stay available. Those
   * places are named by the application's own labels, which only the application knows.
   */
  optOutHint?: TQTooltipBinding,
  /** The page that shows the highlights of all releases. */
  allReleasesLink: RouteLocationRaw,
}>();

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void,
  (e: 'update:hasOptedOut', value: boolean): void,
  /** Emitted whenever the modal closes: by the close button, the X, Escape or the link to all releases. */
  (e: 'close'): void,
}>();

const {
  t,
} = useLibraryI18n('components/release-highlights/modal-release-highlights');

type TSlide = {
  type: 'highlight',
  version: string,
  highlight: TReleaseHighlight,
} | {
  type: 'releaseNotes',
  version: string,
  releaseNotes: string,
};

const selectedReleases = computed<TRelease[]>(() => selectReleases({
  releases: props.releases,
  currentVersion: props.currentVersion,
  lastKnownVersion: props.lastKnownVersion,
}));

/** The highlights of all selected releases, newest release first, each followed by its release notes. */
const slides = computed<TSlide[]>(() => selectedReleases.value.flatMap((release) => {
  const highlightSlides: TSlide[] = release.highlights.map((highlight) => ({
    type: 'highlight',
    version: release.version,
    highlight,
  }));
  if (release.releaseNotes === undefined) {
    return highlightSlides;
  }
  return [
    ...highlightSlides,
    {
      type: 'releaseNotes',
      version: release.version,
      releaseNotes: release.releaseNotes,
    },
  ];
}));

const title = computed<string>(() => {
  const newestRelease = selectedReleases.value.at(0);
  if (newestRelease === undefined) {
    return t('title');
  }
  return t('titleWithVersion', {
    version: newestRelease.version,
  });
});

const shownSlideIndex = shallowRef(0);

watch(
  () => props.visible,
  (isVisible) => {
    if (isVisible) {
      shownSlideIndex.value = 0;
    }
  },
);
</script>

<style scoped lang="scss">
.modal-release-highlights {
  // The release notes are often much longer than a highlight. They take no height of their own and
  // scroll within the height of the highlights instead.
  &__release-notes {
    position: relative;
    height: 100%;
    min-height: 16rem;
  }

  &__release-notes-content {
    position: absolute;
    inset: 0;
    overflow-y: auto;
  }

  &__actions {
    // The checkbox stays on the left, the link and the button on the right.
    > :first-child {
      margin-right: auto;
    }
  }
}
</style>
